/**
 * Dashboard session 持久化与恢复
 *
 * 术语：
 * - dashboardInstance：Vue 组件实例（dashboardScript.js 混入 routeSyncMixin 后的 this），
 *   通过它读写页面状态（curCategory、date、commonParams）、$refs 以及 syncRouteByReplace 等方法
 * - session：写入 sessionStorage 的快照，key = `${email}_msn_new_dashboard_ctx`
 *
 * session 结构（collectDashboardSessionState 产出）：
 * {
 *   timezone, date, latitude, curTrend,
 *   breadcrumbs: SearchPanel 面包屑,
 *   hierarchy: { curCategory, accountId, campaignId, singleCapFlag, campaignName? },
 *   viewSelected: campaign 多选上下文（进入 ad 层时的 campaign 筛选条件）,
 *   tab: { value, isDomain, dimensionType }
 * }
 */
import Cookie from 'js-cookie';
import moment from 'moment';
import { timezoneList } from '@/modules/msn/constants/';
import { QUERY_KEYS, DASHBOARD_SESSION_KEY_SUFFIX } from './constants';
import { getQueryAccountId, getQueryCampaignId, getQueryLatitude, hasAccountAccess } from './normalizeQuery';
import { parseDashboardQuery } from './parseQuery';
import { resolveBreadcrumbSingleCapFlag, buildBreadcrumbsFromState } from './breadcrumbs';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const TREND_COOKIE_KEY = 'new_db_trend';

// ─────────────────────────────────────────────────────
// 通用工具
// ─────────────────────────────────────────────────────

/** 面包屑项序列化：剥离 Vue 响应式包装，只保留可 JSON 存储的纯对象 */
function serializeBreadcrumb(breadcrumb) {
    if (!breadcrumb) return null;
    return {
        type: breadcrumb.type, id: breadcrumb.id, name: breadcrumb.name,
        num: breadcrumb.num, tab: breadcrumb.tab,
        origin: breadcrumb.origin ? { ...breadcrumb.origin } : {}
    };
}

/** 面包屑数组深拷贝（用于写回 SearchPanel，切断引用） */
function cloneBreadcrumbs(crumbs) {
    return (crumbs || []).map(item => ({ ...item, origin: item.origin ? { ...item.origin } : {} }));
}

// ─────────────────────────────────────────────────────
// 层级解析
// ─────────────────────────────────────────────────────

/**
 * 从面包屑数组反推层级信息
 *
 * 面包屑 [accountCrumb, campaignCrumb?, adCrumb?] → { curCategory, accountId, campaignId, singleCapFlag }
 * SearchPanel 默认的「全部账户」不在 breadcrumbs 中，此处手动补入 root 项以计算 singleCapFlag
 *
 * @param {Array} breadcrumbs SearchPanel.breadcrumbs（不含默认「全部账户」）
 * @returns {Object|null}
 */
export function resolveHierarchyFromBreadcrumbs(breadcrumbs) {
    if (!Array.isArray(breadcrumbs) || !breadcrumbs.length) return null;
    const active = breadcrumbs[breadcrumbs.length - 1];
    if (!active?.type) return null;

    const origin = active.origin || {};
    // 补入 root 项用于 resolveBreadcrumbSingleCapFlag 计算
    const withRoot = [{ type: 'account' }, ...breadcrumbs];
    const singleCapFlag = resolveBreadcrumbSingleCapFlag(active, withRoot, breadcrumbs.length);

    return {
        curCategory: active.type,
        accountId: origin.accountId || '',
        campaignId: origin.campaignId || '',
        singleCapFlag
    };
}

/**
 * 从 session 解析层级（兼容旧数据无 hierarchy 字段）
 *
 * 优先级：breadcrumbs 反推 > hierarchy 直取 > viewSelected + tab 推断
 */
export function resolveSessionHierarchy(session) {
    // 优先从面包屑反推
    const fromCrumbs = resolveHierarchyFromBreadcrumbs(session?.breadcrumbs);
    if (fromCrumbs) return fromCrumbs;

    // 直接读 hierarchy
    if (session?.hierarchy?.curCategory) return { ...session.hierarchy };

    // 兼容旧数据：从 viewSelected + tab 推断
    const viewSelected = session?.viewSelected;
    const tabState = session?.tab || {};
    const hasCampaignContext = !!(viewSelected?.campaignIdForAdList || viewSelected?.curSelectCampaignInfo?.length);
    const isDomainTab = tabState.isDomain || ['domain', 'platform'].includes(tabState.value);
    if (!hasCampaignContext && !isDomainTab) return null;

    return {
        curCategory: tabState.value === 'ad' ? 'ad' : (session?.hierarchy?.curCategory || 'campaign'),
        accountId: viewSelected?.accountId || session?.hierarchy?.accountId || '',
        campaignId: viewSelected?.campaignIdForAdList
            || (viewSelected?.curSelectCampaignInfo || []).map(item => item.id).join(',')
            || session?.hierarchy?.campaignId || '',
        singleCapFlag: session?.hierarchy?.singleCapFlag !== false
    };
}

// ─────────────────────────────────────────────────────
// Session 读写
// ─────────────────────────────────────────────────────

/** 从 dashboard 实例获取当前用户 email（作为 session key 的一部分） */
export function getDashboardSessionEmail(inst) {
    const email = inst?.$store?.getters['msn/getEmail'];
    return email ? String(email) : '';
}

/** 生成 sessionStorage key */
export function getDashboardSessionKey(email) {
    return email ? `${email}_${DASHBOARD_SESSION_KEY_SUFFIX}` : '';
}

/** 清除指定用户的 session */
export function clearDashboardSession(email) {
    const key = getDashboardSessionKey(email);
    if (key) sessionStorage.removeItem(key);
}

/**
 * 读取 session
 * @param {Object|string} instOrEmail  dashboard 实例或 email 字符串
 * @returns {Object|null}
 */
export function readDashboardSession(instOrEmail) {
    const email = typeof instOrEmail === 'object' ? getDashboardSessionEmail(instOrEmail) : instOrEmail;
    const key = getDashboardSessionKey(email);
    if (!key) return null;
    try { return JSON.parse(sessionStorage.getItem(key)); }
    catch { return null; }
}

/** 写入 session */
export function writeDashboardSession(instOrEmail, payload) {
    const email = typeof instOrEmail === 'object' ? getDashboardSessionEmail(instOrEmail) : instOrEmail;
    const key = getDashboardSessionKey(email);
    if (!key) return;
    try { sessionStorage.setItem(key, JSON.stringify(payload)); }
    catch { /* sessionStorage 配额满时忽略 */ }
}

/**
 * 创建广告成功后：将 session 层级设为指定账户的 campaign 列表
 *
 * 效果：下次进入控制台（无 URL 层级）时自动恢复到该账户的 campaign 视图，
 * 而不是停留在创建前的旧账户或全部账户页。
 *
 * @param {Object} appInstance  具备 $store 的 Vue 实例（如 createScript）
 * @param {string|number} accountId  创建广告使用的账户 ID
 */
export function setDashboardSessionHierarchyForAccount(appInstance, accountId) {
    const email = getDashboardSessionEmail(appInstance);
    const normalizedAccountId = accountId != null && accountId !== '' ? String(accountId) : '';
    if (!email || !normalizedAccountId) return;

    const accountList = appInstance.$store?.getters?.['msn/getAccountList'] || [];
    const accountOption = accountList.find(item => String(item.value) === normalizedAccountId);
    const accountName = accountOption?.label || normalizedAccountId;

    const breadcrumbs = buildBreadcrumbsFromState({
        type: 'campaign', accountId: normalizedAccountId, accountName: accountName, singleCapFlag: true
    }).map(serializeBreadcrumb).filter(Boolean);

    const existing = readDashboardSession(appInstance) || {};
    writeDashboardSession(email, {
        ...existing,
        breadcrumbs,
        hierarchy: { curCategory: 'campaign', accountId: normalizedAccountId, campaignId: '', singleCapFlag: true },
        viewSelected: null,
        tab: { value: 'campaign', isDomain: false, dimensionType: 'domainName' }
    });

    // 同步全局账户选择，避免 Dashboard userAccount watch 用旧账户覆盖恢复结果
    appInstance.$store?.commit?.('msn/SET_USER_ACCOUNT', { accountId: normalizedAccountId, accountName: accountName });
}

/** 登出时清除 session（无 email 时按后缀匹配批量清除） */
export function clearDashboardSessionOnLogout(email) {
    if (email) { clearDashboardSession(email); return; }
    const suffix = `_${DASHBOARD_SESSION_KEY_SUFFIX}`;
    Object.keys(sessionStorage).forEach(key => { if (key.endsWith(suffix)) sessionStorage.removeItem(key); });
}

// ─────────────────────────────────────────────────────
// URL 层级判断
// ─────────────────────────────────────────────────────

/**
 * URL 是否携带显式层级参数
 *
 * 有 ac_id / c_id，或 type 为 campaign / ad，即视为 URL 自带层级，
 * 应优先走 URL 恢复而非 session 恢复
 */
export function hasExplicitHierarchyInQuery(query = {}) {
    if (getQueryAccountId(query) || getQueryCampaignId(query)) return true;
    const type = query[QUERY_KEYS.TYPE];
    return type === 'ad' || type === 'campaign';
}

// ─────────────────────────────────────────────────────
// 面包屑采集
// ─────────────────────────────────────────────────────

/** 根据 dashboard 当前内存状态推断面包屑（$refs 不可用时兜底） */
function buildBreadcrumbsFromDashboardState(inst) {
    const curCategory = inst.curCategory || 'account';
    const { accountId, campaignId, singleCapFlag } = inst.commonParams || {};
    if (curCategory === 'account' || !accountId) return [];

    const accountInfo = (inst.accountList || []).find(item => String(item.value) === String(accountId));
    const accountDisplayName = accountInfo?.label || String(accountId);

    // 单 campaign 时优先从 session 取真实名称，避免面包屑显示 campaignId
    let campaignName = campaignId ? String(campaignId) : '';
    if (campaignId && !String(campaignId).includes(',')) {
        const existing = readDashboardSession(inst);
        if (existing?.hierarchy?.campaignName) campaignName = existing.hierarchy.campaignName;
    }

    return buildBreadcrumbsFromState({
        type: curCategory,
        accountId: String(accountId),
        accountName: accountDisplayName,
        campaignId: campaignId ? String(campaignId) : '',
        campaignName,
        singleCapFlag: singleCapFlag !== false
    });
}

/**
 * 收集面包屑（优先级：SearchPanel $refs > 内存缓存 > commonParams 推断）
 * 同时回写 _sessionBreadcrumbs 供后续使用
 */
function collectBreadcrumbsFromDashboard(inst) {
    const searchPanelCrumbs = inst.$refs?.searchBar?.breadcrumbs;

    if (Array.isArray(searchPanelCrumbs)) {
        const serialized = searchPanelCrumbs.length
            ? searchPanelCrumbs.map(serializeBreadcrumb).filter(Boolean)
            : [];
        inst._sessionBreadcrumbs = serialized;
        return serialized;
    }

    if (inst._sessionBreadcrumbs?.length) return inst._sessionBreadcrumbs;

    const inferred = buildBreadcrumbsFromDashboardState(inst)
        .map(serializeBreadcrumb).filter(Boolean);
    if (inferred.length) inst._sessionBreadcrumbs = inferred;
    return inferred;
}

/** 层级变更后从 SearchPanel 同步面包屑到 _sessionBreadcrumbs 缓存 */
export function syncSessionBreadcrumbsCache(inst) {
    collectBreadcrumbsFromDashboard(inst);
}

// ─────────────────────────────────────────────────────
// Session parsed 构建
// ─────────────────────────────────────────────────────

/** 从 session 面包屑构建 parsed（不依赖 accountList） */
export function buildParsedFromSessionBreadcrumbs(session) {
    const crumbs = session?.breadcrumbs;
    if (!Array.isArray(crumbs) || !crumbs.length) return null;

    const hierarchy = resolveHierarchyFromBreadcrumbs(crumbs);
    if (!hierarchy) return null;

    const active = crumbs[crumbs.length - 1];
    const category = {
        type: active.type,
        id: active.id || active.origin?.campaignId || active.origin?.accountId || '',
        name: active.name,
        origin: { ...(active.origin || {}) }
    };
    if (active.num) category.num = active.num;

    return {
        category,
        singleCapFlag: hierarchy.singleCapFlag !== false,
        breadcrumbs: cloneBreadcrumbs(crumbs)
    };
}

/**
 * 从 session 层级构建与 parseDashboardQuery 相同结构的 parsed
 * accountList 未就绪时回退到 buildParsedFromSessionBreadcrumbs
 */
export function buildParsedFromSessionHierarchy(session, accountList = []) {
    const hierarchy = resolveSessionHierarchy(session);
    if (!hierarchy?.curCategory) return buildParsedFromSessionBreadcrumbs(session);

    const resolved = { ...hierarchy };
    // 单账户场景：session 可能没存 accountId，用唯一账户补全
    if (!resolved.accountId && resolved.curCategory !== 'account' && accountList?.length === 1) {
        resolved.accountId = String(accountList[0].value);
    }

    const { curCategory, accountId, campaignId, singleCapFlag } = resolved;
    const query = { [QUERY_KEYS.TYPE]: curCategory };
    if (curCategory !== 'account' && accountId) query[QUERY_KEYS.ACCOUNT_ID] = accountId;
    if (campaignId && (curCategory === 'ad' || singleCapFlag === false)) {
        query[QUERY_KEYS.CAMPAIGN_ID] = campaignId;
    }

    return parseDashboardQuery(query, accountList) || buildParsedFromSessionBreadcrumbs(session);
}

// ─────────────────────────────────────────────────────
// Bootstrap & 延迟恢复
// ─────────────────────────────────────────────────────

/**
 * created 阶段预写入层级与面包屑缓存
 * 在 SearchPanel 挂载前设置 curCategory / commonParams，
 * 避免子组件挂载时面包屑被重置为空
 *
 * @returns {boolean} 是否成功写入
 */
export function bootstrapDashboardEntryFromSession(inst, session = readDashboardSession(inst)) {
    if (!session) return false;

    const hierarchy = resolveSessionHierarchy(session);
    if (session.breadcrumbs?.length) {
        inst._sessionBreadcrumbs = session.breadcrumbs.map(serializeBreadcrumb).filter(Boolean);
    }

    if (!hierarchy?.curCategory || hierarchy.curCategory === 'account') {
        return !!inst._sessionBreadcrumbs?.length;
    }

    inst.curCategory = hierarchy.curCategory;
    inst.commonParams = {
        accountId: hierarchy.accountId || '',
        campaignId: hierarchy.campaignId || '',
        singleCapFlag: hierarchy.singleCapFlag !== false
    };

    if (!inst._sessionBreadcrumbs?.length) {
        const inferred = buildBreadcrumbsFromDashboardState(inst)
            .map(serializeBreadcrumb).filter(Boolean);
        if (inferred.length) inst._sessionBreadcrumbs = inferred;
    }
    return true;
}

/**
 * accountList 未就绪时是否需要延迟 session 深层级恢复
 * - account 层级无需延迟
 * - accountList 为 null（未加载）→ 延迟
 * - accountList 为空数组（已加载无权限）→ 不延迟
 */
function needsDeferSessionHierarchyRestore(hierarchy, accountList) {
    if (!hierarchy?.curCategory || hierarchy.curCategory === 'account') return false;
    if (!Array.isArray(accountList)) return true;    // 未加载
    if (!accountList.length) return false;            // 已加载但无权限
    if (!hierarchy.accountId) return accountList.length !== 1;
    return hasAccountAccess(accountList, hierarchy.accountId) === null;
}

// ─────────────────────────────────────────────────────
// Tab 状态管理
// ─────────────────────────────────────────────────────

function findTabInHeaders(inst, tabValue) {
    return (inst.tabHeaders || []).find(tabOption => tabOption.value === tabValue) || null;
}

/** 解析 session 中的 domain/platform tab 值 */
export function resolveDomainTabFromSession(session) {
    const tab = session?.tab || {};
    if (['domain', 'platform'].includes(tab.value)) return tab.value;
    if (tab.isDomain) return tab.dimensionType === 'platform' ? 'platform' : 'domain';
    return null;
}

/** 应用 domain/platform tab UI 状态（不触发列表请求） */
export function applyDomainTabState(inst, tabValue) {
    inst.isDomain = true;
    inst.dimensionType = tabValue === 'platform' ? 'platform' : 'domainName';
    if (inst.$refs.dashbordTab) inst.$refs.dashbordTab.lastVal = tabValue;
}

/** 标记 session 恢复结束，允许子列表发起请求 */
export function finishSessionRestore(inst) {
    inst._isRestoringSession = false;
    inst._dashboardInitialized = true;
}

/** 获取当前层级对应的 tab 值 */
function getHierarchyTabValue(inst) {
    if (inst.isDomain) return inst.dimensionType === 'platform' ? 'platform' : 'domain';
    return inst.curCategory || 'account';
}

/** 将 MsnTab 的 lastVal 与当前层级对齐 */
function syncTabUiToHierarchy(inst) {
    if (inst.$refs.dashbordTab) {
        inst.$refs.dashbordTab.lastVal = getHierarchyTabValue(inst);
    }
}

/** session 恢复 tab 后同步 URL */
function syncUrlIfNeeded(inst) {
    if (inst._syncUrlAfterSessionRestore && typeof inst.syncRouteByReplace === 'function') {
        inst.syncRouteByReplace({ force: true });
        inst._syncUrlAfterSessionRestore = false;
    }
}

/** session 恢复完成后刷新列表 / 图表 */
function refreshAfterSession(inst) {
    if (typeof inst.finishDashboardInit === 'function') {
        inst.finishDashboardInit();
        return;
    }
    inst.$nextTick(() => {
        inst.$nextTick(() => {
            const listRef = inst.$refs?.[inst.curCategory];
            if (listRef && typeof listRef.getSummary === 'function') listRef.getSummary(0);
        });
    });
}

/** 同步恢复 tab 状态（不触发数据请求，仅 UI 对齐） */
function applySessionTabStateSync(inst, session) {
    const domainTab = resolveDomainTabFromSession(session);
    if (!domainTab || !findTabInHeaders(inst, domainTab)) {
        syncTabUiToHierarchy(inst);
        return;
    }
    if (getHierarchyTabValue(inst) === domainTab && inst.isDomain) {
        syncTabUiToHierarchy(inst);
        return;
    }
    if (typeof inst.updateTabState === 'function') {
        inst.updateTabState(findTabInHeaders(inst, domainTab));
    } else {
        applyDomainTabState(inst, domainTab);
    }
}

/**
 * 异步恢复 domain/platform tab，完成后刷新 domain 数据
 * @returns {boolean} 是否成功应用 tab
 */
export function applySessionTab(inst, session) {
    const domainTab = resolveDomainTabFromSession(session);

    if (!domainTab) {
        syncTabUiToHierarchy(inst);
        syncUrlIfNeeded(inst);
        return true;
    }

    const tab = findTabInHeaders(inst, domainTab);
    if (!tab) {
        syncTabUiToHierarchy(inst);
        syncUrlIfNeeded(inst);
        return false;
    }

    const alreadyOn = getHierarchyTabValue(inst) === domainTab && inst.isDomain;
    if (!alreadyOn) {
        inst._isRestoringFromQuery = true;
        if (typeof inst.updateTabState === 'function') inst.updateTabState(tab);
        else applyDomainTabState(inst, domainTab);
    } else {
        syncTabUiToHierarchy(inst);
    }

    // domain 数据刷新和标记清除统一由 finishDashboardInit → clearRestoreFlagsAfterFetch 负责，
    // 此处仅负责 tab UI 状态切换，不触发 refreshDomainData 和 finishSessionRestore，
    // 避免与 finishDashboardInit 重复请求和过早清除恢复标记
    inst.$nextTick(() => {
        inst.$nextTick(() => {
            inst._isRestoringFromQuery = false;
            syncUrlIfNeeded(inst);
        });
    });
    return true;
}

// ─────────────────────────────────────────────────────
// 筛选恢复
// ─────────────────────────────────────────────────────

/** 标记 URL query 中已提供的筛选字段，恢复时避免用 session 覆盖 */
export function getQueryProvidedFilterFields(query = {}) {
    return {
        timezone: !!query[QUERY_KEYS.TIMEZONE],
        startDate: !!query[QUERY_KEYS.START_DATE],
        endDate: !!query[QUERY_KEYS.END_DATE],
        latitude: !!getQueryLatitude(query)
    };
}

/**
 * 用 session 补全筛选（日期/时区/粒度/趋势）
 *
 * urlProvided 标记 URL 已有字段，避免覆盖分享链接中的参数。
 * 例如 URL 有 startDate + endDate 时，不会用 session 中的日期覆盖。
 */
function applySessionFilters(inst, session, urlProvided = {}, options = {}) {
    if (!session) return;
    inst._isRestoringFromQuery = true;

    // 粒度
    const resolvedLatitude = !urlProvided.latitude && session.latitude
        ? session.latitude
        : (getQueryLatitude(inst.$route?.query) || inst.latitude || '1');
    if (!urlProvided.latitude && session.latitude) inst.latitude = session.latitude;

    // 日期
    let datesApplied = false;
    if (!urlProvided.startDate && !urlProvided.endDate && session.date?.[0] && session.date?.[1]) {
        inst.date = [session.date[0], session.date[1]];
        datesApplied = true;
    }
    // 小时模式下校正日期范围下限
    if ((resolvedLatitude || inst.latitude || '1') === '2' && datesApplied && inst.handleHourDateFormatMin) {
        inst.handleHourDateFormatMin();
    }

    // 时区
    if (!urlProvided.timezone && session.timezone
        && timezoneList.some(option => option.value === session.timezone)) {
        inst.timezone = session.timezone;
    }

    // 同步 filterData
    if (inst.date?.[0] && inst.date?.[1]) {
        inst.setFilterData({
            date: [moment(inst.date[0]).format(DATE_TIME_FORMAT), moment(inst.date[1]).format(DATE_TIME_FORMAT)],
            latitude: inst.latitude,
            timezone: inst.timezone
        });
    }

    // 趋势图指标
    if (session.curTrend) {
        Cookie.set(TREND_COOKIE_KEY, session.curTrend);
        if (inst.trendAllData?.length && inst.handleTrendColChange) {
            inst.handleTrendColChange(session.curTrend);
        } else {
            inst.curTrend = session.curTrend.includes(',') ? session.curTrend.split(',') : session.curTrend;
        }
    }

    if (!options.keepRestoringFlag) {
        inst.$nextTick(() => { inst._isRestoringFromQuery = false; });
    }
}

// ─────────────────────────────────────────────────────
// viewSelected & 面包屑恢复
// ─────────────────────────────────────────────────────

/** 恢复 campaign 多选上下文（ad 层级多 campaign 选择时使用） */
function applySessionViewSelected(inst, session) {
    if (!session?.viewSelected?.curSelectCampaignInfo?.length) return;
    // 全部 campaign 列表模式下不恢复，防止残留 curSelectCampaignInfo 覆盖已清除的状态
    const { commonParams } = inst;
    if (commonParams?.singleCapFlag && !commonParams?.campaignId) return;

    const ref = inst.$refs?.campaign;
    if (!ref) return;
    ref.curSelectCampaignInfo = session.viewSelected.curSelectCampaignInfo.map(item => ({ id: item.id, name: item.name }));
    ref.campaignIdForAdList = session.viewSelected.campaignIdForAdList
        || session.viewSelected.curSelectCampaignInfo.map(item => item.id).join(',');
}

/** 将 session 中的面包屑写回 SearchPanel */
function applySessionBreadcrumbs(inst, session) {
    const crumbs = session?.breadcrumbs;
    if (!Array.isArray(crumbs) || !crumbs.length) return;

    const restored = cloneBreadcrumbs(crumbs);
    inst._sessionBreadcrumbs = crumbs.map(serializeBreadcrumb).filter(Boolean);

    const apply = () => {
        if (!inst.$refs?.searchBar) return false;
        inst.$refs.searchBar.breadcrumbs = restored;
        const last = crumbs[crumbs.length - 1];
        if (last?.type) inst.$refs.searchBar.activePath = last.type;
        return true;
    };

    if (!apply()) inst.$nextTick(apply);
}

/** session 无日期时补默认 7 天 */
function ensureSessionDates(inst, applyDefault) {
    if (inst.date?.[0] && inst.date?.[1]) return;
    if (typeof applyDefault === 'function') applyDefault();
}

// ─────────────────────────────────────────────────────
// 完整 session 恢复（URL 无显式层级时）
// ─────────────────────────────────────────────────────

/**
 * 从 session 完整恢复 → replace URL → 刷新数据
 *
 * 流程：
 * 1. 读 session，判断 accountList 是否就绪（深层级需账户权限校验）
 * 2. 预写面包屑缓存（_sessionBreadcrumbs），确保 SearchPanel mounted 时有数据
 * 3. 构建 parsed → applyHierarchyFromParsed 应用层级
 * 4. 恢复筛选（日期/时区/粒度/趋势）+ tab
 * 5. syncRouteByReplace 将恢复结果写入 URL
 * 6. 恢复面包屑 → viewSelected → 刷新数据
 *
 * @returns {boolean} 是否已开始恢复流程
 */
export function restoreFullDashboardFromSession(inst, options = {}) {
    const { applyDefaultDateRange, urlProvidedFilters } = options;
    const session = readDashboardSession(inst);
    if (!session) return false;

    const hierarchy = resolveSessionHierarchy(session);
    if (needsDeferSessionHierarchyRestore(hierarchy, inst.accountList)) {
        inst._pendingSessionHierarchyRestore = true;
        return false;
    }

    inst._isRestoringSession = true;
    inst._isRestoringFromQuery = true;

    // 预写面包屑缓存
    if (session.breadcrumbs?.length) {
        inst._sessionBreadcrumbs = session.breadcrumbs.map(serializeBreadcrumb).filter(Boolean);
    }
    applySessionBreadcrumbs(inst, session);

    // 构建并应用层级
    let parsed = buildParsedFromSessionHierarchy(session, inst.accountList);
    if (!parsed) {
        const fallbackHierarchy = resolveSessionHierarchy(session);
        if (fallbackHierarchy?.curCategory && fallbackHierarchy.curCategory !== 'account') {
            bootstrapDashboardEntryFromSession(inst, session);
            applySessionBreadcrumbs(inst, session);
            applySessionFilters(inst, session, urlProvidedFilters || {}, { keepRestoringFlag: true });
            ensureSessionDates(inst, applyDefaultDateRange);
            inst._pendingSessionHierarchyRestore = true;
            return false;
        }
        parsed = parseDashboardQuery({ [QUERY_KEYS.TYPE]: 'account' }, inst.accountList);
    }

    if (parsed && typeof inst.applyHierarchyFromParsed === 'function') {
        inst.applyHierarchyFromParsed(parsed, null);
    }
    applySessionBreadcrumbs(inst, session);

    // 恢复筛选 + tab
    applySessionFilters(inst, session, urlProvidedFilters || {}, { keepRestoringFlag: true });
    ensureSessionDates(inst, applyDefaultDateRange);
    applySessionTabStateSync(inst, session);

    // replace URL 并刷新数据
    // finishSessionRestore 不在此处调用，统一由 refreshAfterSession → finishDashboardInit → clearRestoreFlagsAfterFetch 清除，
    // 避免标记过早清除导致子组件 watcher 在数据拉取前放行
    const onComplete = () => {
        inst._isRestoringFromQuery = false;
        inst.$nextTick(() => {
            applySessionBreadcrumbs(inst, session);
            applySessionViewSelected(inst, session);
            refreshAfterSession(inst);
        });
    };

    if (typeof inst.syncRouteByReplace === 'function') {
        inst.syncRouteByReplace({ force: true }, onComplete);
    } else {
        onComplete();
    }
    return true;
}

/**
 * URL 已有层级时：仅用 session 补全缺失的筛选/tab 偏好
 *
 * 与 restoreFullDashboardFromSession 的区别：
 * - 完整恢复：URL 无层级 → 恢复层级 + 筛选 + tab
 * - 本函数：URL 有层级（已由 restoreFromQuery 处理）→ 仅补全筛选偏好和 tab
 */
export function restoreDashboardSession(inst, query = inst.$route?.query || {}, options = {}) {
    const { syncUrl = false, filtersFromSession = false } = options;
    const session = readDashboardSession(inst);
    if (!session) return false;

    inst._isRestoringSession = true;
    if (syncUrl) inst._syncUrlAfterSessionRestore = true;

    const urlProvided = filtersFromSession ? {} : getQueryProvidedFilterFields(query);
    applySessionFilters(inst, session, urlProvided);

    inst.$nextTick(() => {
        inst.$nextTick(() => {
            applySessionTab(inst, session);
            applySessionViewSelected(inst, session);
        });
    });
    return true;
}

// ─────────────────────────────────────────────────────
// Session 持久化（写入 sessionStorage）
// ─────────────────────────────────────────────────────

/**
 * 收集 dashboard 当前运行时状态为 session 快照
 *
 * 采集项：
 * 1. curTrend：趋势图指标（优先组件状态，回退到 Cookie）
 * 2. breadcrumbs：优先 SearchPanel $refs → 内存缓存 → commonParams 推断
 * 3. hierarchy：优先面包屑反推 → commonParams 直取
 * 4. campaignName：单 campaign 时从面包屑提取真实名称，刷新后面包屑能正确显示
 * 5. viewSelected：campaign 多选上下文
 * 6. tab：当前 tab 值及 domain/platform 维度
 */
export function collectDashboardSessionState(inst) {
    // 趋势图指标
    const trendCookie = Cookie.get(TREND_COOKIE_KEY) || '';
    const curTrend = typeof inst.curTrend === 'string'
        ? inst.curTrend
        : (Array.isArray(inst.curTrend) ? inst.curTrend.join(',') : trendCookie);

    // 面包屑
    const breadcrumbs = collectBreadcrumbsFromDashboard(inst);
    const hierarchyFromCrumbs = resolveHierarchyFromBreadcrumbs(breadcrumbs);

    // campaign 多选上下文
    let viewSelected = null;
    const campaignRef = inst.$refs?.campaign;
    if (campaignRef?.curSelectCampaignInfo?.length) {
        viewSelected = {
            accountId: inst.commonParams?.accountId || '',
            curSelectCampaignInfo: campaignRef.curSelectCampaignInfo.map(item => ({ id: item.id, name: item.name })),
            campaignIdForAdList: campaignRef.campaignIdForAdList || ''
        };
    } else if (inst.commonParams?.campaignId) {
        viewSelected = {
            accountId: inst.commonParams?.accountId || '',
            curSelectCampaignInfo: [{ id: inst.commonParams.campaignId, name: inst.commonParams.campaignId }],
            campaignIdForAdList: inst.commonParams.campaignId
        };
    }

    // tab 状态
    let tabValue = inst.curCategory || 'account';
    if (inst.isDomain) tabValue = inst.dimensionType === 'platform' ? 'platform' : 'domain';

    // 单 campaign 时提取真实名称（URL 只存 ID，刷新后面包屑需要从 session 恢复真实名称）
    const sessionCampaignId = (hierarchyFromCrumbs || inst.commonParams || {}).campaignId || '';
    let campaignName = '';
    if (sessionCampaignId && !String(sessionCampaignId).includes(',')) {
        const matchedCrumb = breadcrumbs.find(
            breadcrumbItem => String(breadcrumbItem.origin?.campaignId) === String(sessionCampaignId)
                && breadcrumbItem.name && breadcrumbItem.name !== String(sessionCampaignId)
        );
        campaignName = matchedCrumb?.name || '';
        // 面包屑名称已退化为 ID（刷新后 URL 恢复场景），从已有 session 保留上次名称
        if (!campaignName) {
            const existing = readDashboardSession(inst);
            if (existing?.hierarchy?.campaignName) campaignName = existing.hierarchy.campaignName;
        }
    }

    const hierarchy = hierarchyFromCrumbs || {
        curCategory: inst.curCategory || 'account',
        accountId: inst.commonParams?.accountId || '',
        campaignId: inst.commonParams?.campaignId || '',
        singleCapFlag: inst.commonParams?.singleCapFlag !== false
    };
    if (campaignName) hierarchy.campaignName = campaignName;

    return {
        timezone: inst.timezone || '',
        date: (inst.date?.[0] && inst.date?.[1]) ? [inst.date[0], inst.date[1]] : null,
        latitude: inst.latitude || '1',
        curTrend: curTrend || '',
        breadcrumbs,
        hierarchy,
        viewSelected,
        tab: {
            value: tabValue,
            isDomain: !!inst.isDomain,
            dimensionType: inst.dimensionType || 'domainName'
        }
    };
}

/**
 * 将 dashboard 当前状态写入 sessionStorage
 *
 * 保护策略（避免中间态覆盖有效数据）：
 * - 恢复中（_isRestoringFromQuery / _isSyncingRoute / _isRestoringSession）不写入
 * - 未初始化（!_dashboardInitialized）不写入
 * - force=true 跳过保护（用于 beforeDestroy / beforeRouteLeave）
 *
 * 面包屑兜底：离开页面时 $refs 可能已销毁导致面包屑采集为空，
 * 此时沿用 sessionStorage 中已有的面包屑（主动回首页除外）
 */
export function persistDashboardSession(inst, options = {}) {
    const { force = false } = options;
    if (!force && (inst._isRestoringFromQuery || inst._isSyncingRoute || inst._isRestoringSession)) return;
    if (!force && !inst._dashboardInitialized) return;

    const email = getDashboardSessionEmail(inst);
    if (!email) return;

    const state = collectDashboardSessionState(inst);

    // 离开页面时 $refs 销毁导致面包屑为空：沿用已有 session 面包屑
    // 但主动回「全部账户」首页时不保留旧层级
    const isAccountHome = (inst.curCategory || 'account') === 'account' && !inst.commonParams?.accountId;
    if (force && !state.breadcrumbs?.length && !isAccountHome) {
        const existing = readDashboardSession(inst);
        if (existing?.breadcrumbs?.length) {
            state.breadcrumbs = existing.breadcrumbs;
            const existingHierarchy = resolveHierarchyFromBreadcrumbs(existing.breadcrumbs);
            if (existingHierarchy) state.hierarchy = existingHierarchy;
        }
    }

    writeDashboardSession(email, state);
}
